import { useState } from 'react';
import { ChevronDown, ChevronUp, Filter as FilterIcon, ArrowLeft, Palette, Group, Pin } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ColumnDef, DateFilter, SortState } from './types';
import { GroupMode } from './savedViews';
import { ColumnFilterControls } from './ColumnFilterControls';

export type SortAction = 'replace-asc' | 'replace-desc' | 'append-asc' | 'append-desc' | 'remove';

interface ColumnHeaderMenuProps {
  column: ColumnDef;
  sort: SortState;
  filterValues: Set<string> | undefined;
  dateFilter: DateFilter | undefined;
  uniqueValues: string[];
  showTotal: boolean;
  hasFormat: boolean;
  isGroupedBy: boolean;
  groupMode: GroupMode | undefined;
  isPinnedToFilters: boolean;
  onSort: (action: SortAction) => void;
  onFilterChange: (values: Set<string>) => void;
  onDateFilterChange: (df: DateFilter | null) => void;
  onToggleTotal: () => void;
  onOpenFormat: () => void;
  onToggleGroupBy: () => void;
  onSetGroupMode: (mode: GroupMode) => void;
  onRename: (label: string | undefined) => void;
  onTogglePinFilter: () => void;
}

export function ColumnHeaderMenu({
  column,
  sort,
  filterValues,
  dateFilter,
  uniqueValues,
  showTotal,
  hasFormat,
  isGroupedBy,
  groupMode,
  isPinnedToFilters,
  onSort,
  onFilterChange,
  onDateFilterChange,
  onToggleTotal,
  onOpenFormat,
  onToggleGroupBy,
  onSetGroupMode,
  onRename,
  onTogglePinFilter,
}: ColumnHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<'menu' | 'filter' | 'rename'>('menu');
  const [draftLabel, setDraftLabel] = useState('');

  const sortIndex = sort.findIndex((s) => s.columnId === column.id);
  const isSortedHere = sortIndex >= 0;
  const mySortDir = isSortedHere ? sort[sortIndex].direction : null;
  const isDate = column.dataType === 'date';
  const hasFilter = (filterValues?.size ?? 0) > 0 || !!dateFilter;
  const hasExistingSort = sort.length > 0;

  const doAction = (action: SortAction) => {
    onSort(action);
    setOpen(false);
  };

  const clearFilter = () => {
    onFilterChange(new Set());
    if (isDate) onDateFilterChange(null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setPanel('menu');
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            'group inline-flex items-center gap-1 min-w-0 px-2 py-2 w-full text-left text-[13px] font-medium text-stone-600 hover:bg-stone-50 transition-colors',
            isSortedHere && 'text-[#0078d4]'
          )}
        >
          {hasFilter && <FilterIcon className="w-3 h-3 text-[#0078d4] fill-[#0078d4] shrink-0" />}
          {hasFormat && <Palette className="w-3 h-3 text-stone-500 shrink-0" />}
          {isGroupedBy && <Group className="w-3 h-3 text-amber-500 shrink-0" />}
          {isPinnedToFilters && <Pin className="w-3 h-3 text-[#0078d4] shrink-0" />}
          <span className="truncate min-w-0">{column.label}</span>
          {isSortedHere && (
            <span className="flex items-center gap-0.5 text-[#0078d4]">
              {mySortDir === 'asc' ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
              {sort.length > 1 && (
                <span className="text-[10px] leading-none font-bold">{sortIndex + 1}</span>
              )}
            </span>
          )}
          {!isSortedHere && (
            <ChevronDown
              className={cn(
                'w-3 h-3 text-stone-400 transition-all ml-auto shrink-0 opacity-0 group-hover:opacity-100',
                open && 'rotate-180 opacity-100'
              )}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-56 p-0 rounded-sm">
        {panel === 'menu' ? (
          <div className="py-1">
            <MenuItem
              label={isDate ? 'Older to newer' : 'A to Z'}
              active={isSortedHere && sort.length === 1 && mySortDir === 'asc'}
              onClick={() => doAction('replace-asc')}
            />
            <MenuItem
              label={isDate ? 'Newer to older' : 'Z to A'}
              active={isSortedHere && sort.length === 1 && mySortDir === 'desc'}
              onClick={() => doAction('replace-desc')}
            />
            {hasExistingSort && !isSortedHere && (
              <>
                <MenuItem
                  label="Add to sort ↑"
                  onClick={() => doAction('append-asc')}
                />
                <MenuItem
                  label="Add to sort ↓"
                  onClick={() => doAction('append-desc')}
                />
              </>
            )}
            {isSortedHere && sort.length > 1 && (
              <MenuItem
                label="Remove from sort"
                onClick={() => doAction('remove')}
              />
            )}
            <MenuDivider />
            <MenuItem
              label="Filter by"
              active={hasFilter}
              submenu
              onClick={() => setPanel('filter')}
            />
            <MenuItem
              label={isPinnedToFilters ? 'Unpin from filters pane' : 'Pin to filters pane'}
              active={isPinnedToFilters}
              onClick={() => {
                onTogglePinFilter();
                setOpen(false);
              }}
            />
            <MenuDivider />
            <MenuItem
              label={isGroupedBy ? `Stop grouping by ${column.label}` : `Group by ${column.label}`}
              active={isGroupedBy}
              onClick={() => {
                onToggleGroupBy();
                setOpen(false);
              }}
            />
            {isGroupedBy && (
              <MenuItem
                label={groupMode === 'summary' ? 'Show row details' : 'Summary only'}
                active={groupMode === 'summary'}
                onClick={() => {
                  onSetGroupMode(groupMode === 'summary' ? 'detail' : 'summary');
                  setOpen(false);
                }}
              />
            )}
            <MenuItem
              label={showTotal ? 'Hide total' : 'Totals'}
              active={showTotal}
              onClick={() => {
                onToggleTotal();
                setOpen(false);
              }}
            />
            <MenuDivider />
            <MenuItem
              label="Conditional formatting"
              active={hasFormat}
              onClick={() => {
                onOpenFormat();
                setOpen(false);
              }}
            />
            <MenuItem
              label="Rename column"
              submenu
              onClick={() => {
                setDraftLabel(column.label);
                setPanel('rename');
              }}
            />
          </div>
        ) : panel === 'rename' ? (
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100">
              <button
                className="flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-stone-600"
                onClick={() => setPanel('menu')}
              >
                <ArrowLeft className="w-4 h-4" />
                Rename
              </button>
              <button
                className="text-sm font-medium text-[#0078d4] hover:text-[#106ebe]"
                onClick={() => {
                  onRename(undefined);
                  setOpen(false);
                }}
              >
                Reset
              </button>
            </div>
            <div className="p-2 space-y-2">
              <Input
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = draftLabel.trim();
                    onRename(trimmed ? trimmed : undefined);
                    setOpen(false);
                  }
                  if (e.key === 'Escape') {
                    setPanel('menu');
                  }
                }}
                placeholder={column.label}
                autoFocus
                className="h-8 text-sm"
              />
              <button
                onClick={() => {
                  const trimmed = draftLabel.trim();
                  onRename(trimmed ? trimmed : undefined);
                  setOpen(false);
                }}
                className="w-full h-8 rounded-md bg-[#0078d4] text-white text-xs font-medium hover:bg-[#106ebe]"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col max-h-80">
            <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100">
              <button
                className="flex items-center gap-1.5 text-sm font-semibold text-stone-900 hover:text-stone-600"
                onClick={() => setPanel('menu')}
              >
                <ArrowLeft className="w-4 h-4" />
                Filter by:
              </button>
              <button
                className="text-sm font-medium text-[#0078d4] hover:text-[#106ebe]"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
            <div className="overflow-y-auto py-1.5">
              <ColumnFilterControls
                column={column}
                filterValues={filterValues}
                dateFilter={dateFilter}
                uniqueValues={uniqueValues}
                onFilterChange={onFilterChange}
                onDateFilterChange={onDateFilterChange}
              />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface MenuItemProps {
  label: string;
  active?: boolean;
  submenu?: boolean;
  onClick: () => void;
}

function MenuItem({ label, active, submenu, onClick }: MenuItemProps) {
  return (
    <button
      className={cn(
        'w-full flex items-center justify-between px-3 h-8 text-[13px] text-stone-900 hover:bg-stone-100 transition-colors',
        active && 'bg-[#0078d4]/10 text-[#0078d4] font-medium'
      )}
      onClick={onClick}
    >
      <span className="truncate">{label}</span>
      {submenu && <span className="text-stone-400 text-xs">›</span>}
    </button>
  );
}

function MenuDivider() {
  return <div className="my-1 h-px bg-stone-100" />;
}
